import React, { useEffect, useMemo, useState } from "react";
import { Box, Skeleton, Stack } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useActiveModules, moduleChecker } from "../../../hooks/useClinicModule";
import { LockedByExaminationBanner } from "../../../components/atoms";
import { useAlert } from "../../../context/AlertProvider";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import useTerms from "../../../hooks/useTerms";
import {
  useListClinicPatientLinksQuery,
  useTerminateClinicPatientLinkMutation,
} from "../../../store";
import PatientHeader from "./PatientHeader";
import {
  PersonalInfoTab,
  AppointmentsTab,
  MedicationAlarmsTab,
  PracticeSessionsTab,
  PrescriptionsTab,
  AnamnesisTab,
  InvestigationTab,
  TreatmentsTab,
  DeviceMeasurementsTab,
  NutritionTab,
  LEGACY_NUTRITION_TABS,
  CommunicationTab,
} from "../../../components/organisms/PatientWorkspaceTabs";
import { LEGACY_COMMUNICATION_TABS } from "../../../components/organisms/PatientWorkspaceTabs/CommunicationTab";
import AccountTab from "../../../components/organisms/PatientWorkspaceTabs/AccountTab";
import { COLORS, CARD_PAGE_PAD } from "../../../styles/uiConstants";

import {
  useGetUserMutation,
} from "../../../store/secretaryApi";

// Düz (kutusuz) sekmeler: tek veri akışı / liste | editör — beyaz zemin, kenardan
// kenara; kendi iç hizalarını (px md:4) taşırlar. Diğerleri kartlı: slate50 + CARD_PAGE_PAD.
const FLAT_TABS = new Set([
  "anamnez",
  "tetkik",
  "tedaviler",
  "receteler",
  "beslenme",
  "ilac-alarmlari",
  "iletisim",
  "randevular",
  "uygulamalar",
]);

export default function PatientDetail() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  // Eski mantık `auth.role === "Secretary"/"Doctor"` arıyordu, fakat sistem
  // rolleri Admin / AdminStaff / ClinicStaff. Klinik personelinin tüm
  // sekmelerde düzenleme yapabilmesi için izin bazlı kontrol — her tab
  // mutation'ı zaten kendi requirePermission'unu çalıştırır; bu flag ise
  // tab içindeki form'ların readonly görünüp görünmeyeceğini belirler.
  const isAdmin = auth?.role === "Admin" || auth?.role === "AdminStaff";
  const isClinic = auth?.role === "ClinicStaff";
  // PatientDetail klinik kontekstinde her zaman edit edilebilir; tek tek
  // mutation'lar permission yoksa yine 403 döner ama UI'da disabled kilit
  // istemiyoruz (kullanıcı talebi).
  const canEditPatient = isAdmin || isClinic;
  const { patientId, clinicId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const terms = useTerms();

  // Hastayı klinikten çıkarma: yalnızca klinik bağlamında (admin klinik görünümünde değil)
  const canTerminate = useHasPermission("clinicPatientLink:terminate") && !clinicId;
  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [terminateLink, { isLoading: terminating }] = useTerminateClinicPatientLinkMutation();
  const { data: activeLinkData } = useListClinicPatientLinksQuery(
    { token, group: "active" },
    { skip: !token || !canTerminate }
  );
  const linkId = useMemo(() => {
    const l = (activeLinkData?.data?.links || []).find(
      (x) => String(x.patient?._id || x.patient) === String(patientId)
    );
    return l?._id || null;
  }, [activeLinkData, patientId]);

  const handleTerminate = async () => {
    try {
      await terminateLink({ token, linkId }).unwrap();
      showSuccess(`${terms.patient} klinik kayıtlarından çıkarıldı.`);
      navigate("/Hastalar");
    } catch (e) {
      showError(e?.data?.message || "Çıkarma başarısız.");
    } finally {
      setConfirmTerminate(false);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  // Beslenme (2026-10-07): plan, günlük, su ve öykü tek sekmede; eski ayrı sekme
  // adresleri (?tab=diyet vb.) yeni sekmenin ilgili bölümüne düşer.
  const rawTab = searchParams.get("tab") || "kisisel-bilgiler";
  const legacyNutritionSection = LEGACY_NUTRITION_TABS[rawTab] || null;
  // İletişim (2026-10-07): mesajlar + telefon görüşmeleri + uygulama bildirimleri tek sekmede.
  const legacyCommunicationSection = LEGACY_COMMUNICATION_TABS[rawTab] || null;
  const activeTab = legacyNutritionSection
    ? "beslenme"
    : legacyCommunicationSection
      ? "iletisim"
      : rawTab;
  const communicationSection = legacyCommunicationSection || searchParams.get("bolum");
  const setCommunicationSection = (bolum) => setSearchParams({ tab: "iletisim", bolum }, { replace: true });
  const nutritionSection = legacyNutritionSection || searchParams.get("bolum");
  const setNutritionSection = (bolum) => setSearchParams({ tab: "beslenme", bolum }, { replace: true });
  // ?kayit=<id>: klinik geneli listelerden (Tedaviler) gelince o kayıt seçili açılır
  const initialRecordId = searchParams.get("kayit");
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  // Permission-based tab visibility. Kisisel Bilgiler is always visible.
  // useHasPermission already bypasses for Admin. Sekmeler ayrıca kliniğin
  // modüllerine bakar — personelde izin zaten süzülmüş gelir; admin bir
  // kliniğin hastasını açınca (ClinicModulesContext) o kliniğin modülleri geçerli.
  const hasModule = moduleChecker(useActiveModules());
  const canSeeAnamnez = useHasPermission("anamnez:read") && hasModule("anamnesis");
  const canSeeTetkik = useHasPermission("tetkik:read") && hasModule("investigation");
  const canSeeTreatments = useHasPermission("treatments:read") && hasModule("treatment");
  // Reçeteler sekmesi tedaviden okur; reçete modülü kapalıysa (ör. geleneksel
  // tıp) gizlenir. Diyet sekmesi kendi iznine bağlı (2026-10-05).
  const canSeePrescriptions = canSeeTreatments && hasModule("prescription");
  const canSeeDiet = useHasPermission("diet:read") && hasModule("diet");
  const canSeePhoneCalls = useHasPermission("phoneCalls:read") && hasModule("phoneCalls");
  const canSeeNotifications = useHasPermission("notifications:manage");
  const canSeeMessages = useHasPermission("messages:read") && hasModule("messaging");
  const canSeeAppointments = useHasPermission("appointments:read");
  const canSeeDevices = useHasPermission("measurements:read") && hasModule("devices");
  const canSeeMedPlans = useHasPermission("medication-plans:read") && hasModule("medicationAlarms");
  const canSeeHydration = useHasPermission("hydration:read") && hasModule("hydration");
  const canManageHydration = useHasPermission("hydration:manage");
  const canSeeAccount = useHasPermission("payments:read") && hasModule("billing");
  const canSeeSessions = useHasPermission("sessions:read") && hasModule("traditional");

  const [patient, setPatient] = useState(null);
  // Uygulamaya bağlı bölümler (mesajlar, uygulama bildirimleri, ilaç alarmları, su takibi,
  // hastanın kendi besin kayıtları) yalnız mobil uygulamaya girmiş hastada görünür.
  // Hiç girmemiş hasta (klinikçe açılmış, hasLoggedIn === false) kağıt + klinikteki
  // ölçüm/muayeneyle izlenir; uygulamaya girdiği anda bölümler kendiliğinden açılır.
  const patientUsesApp = patient?.hasLoggedIn !== false;
  const showMedPlans = canSeeMedPlans && patientUsesApp;
  const showHydration = canSeeHydration && patientUsesApp;
  const hasCommunication =
    canSeePhoneCalls || (patientUsesApp && (canSeeMessages || canSeeNotifications));

  const visibleTabs = useMemo(
    () =>
      [
        { value: "kisisel-bilgiler", label: "Kişisel Bilgiler" },
        canSeeAnamnez && { value: "anamnez", label: "Anamnez" },
        canSeeTetkik && { value: "tetkik", label: "Tetkik" },
        canSeeTreatments && { value: "tedaviler", label: "Tedaviler" },
        canSeePrescriptions && { value: "receteler", label: "Reçeteler" },
        (canSeeDiet || showHydration) && { value: "beslenme", label: "Beslenme" },
        showMedPlans && { value: "ilac-alarmlari", label: "İlaç Alarmları" },
        canSeeSessions && { value: "uygulamalar", label: "Uygulamalar" },
        canSeeDevices && { value: "cihazlar", label: "Cihaz Ölçümleri" },
        hasCommunication && { value: "iletisim", label: "İletişim" },
        canSeeAppointments && { value: "randevular", label: "Randevular" },
        canSeeAccount && { value: "hesap", label: "Hesap" },
      ].filter(Boolean),
    [
      canSeeTreatments,
      canSeePrescriptions,
      canSeeDiet,
      canSeeAnamnez,
      canSeeTetkik,
      canSeeAppointments,
      canSeeDevices,
      showMedPlans,
      showHydration,
      canSeeSessions,
      canSeeAccount,
      hasCommunication,
    ]
  );

  // If the currently active tab is no longer visible (e.g. user deep-linked
  // with ?tab=anamnez but lacks anamnez:read), fall back to kisisel-bilgiler.
  useEffect(() => {
    if (!visibleTabs.find((t) => t.value === activeTab)) {
      setActiveTab("kisisel-bilgiler");
    }
    // setActiveTab is a stable closure over setSearchParams; exclude from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, activeTab]);

  const [getUser] = useGetUserMutation();

  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const activeExamination = patient?.activeExamination || null;
  const isLockedByExamination = Boolean(activeExamination);
  const effectiveCanEdit = canEditPatient && !isLockedByExamination;

  useEffect(() => {
    if (!token || !patientId) return;
    setInitialLoadDone(false);
    const load = async () => {
      try {
        // Tanı / alerji seçenekleri Kişisel Bilgiler'in seçim drawer'ında
        // sunucudan sayfalı aranır; burada toplu yüklenmez.
        const patientRes = await getUser({ token, id: patientId }).unwrap();
        setPatient(patientRes?.data?.user || null);
      } catch (_) {
        // Tab-level components surface their own load errors; the header
        // stays empty until a patient is fetched again via refresh.
      } finally {
        setInitialLoadDone(true);
      }
    };
    load();
  }, [token, patientId, getUser]);

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: COLORS.slate50,
        overflow: "hidden",
      }}
    >
      <PatientHeader
        patient={patient}
        onBack={() => navigate(clinicId ? `/Klinikler/${clinicId}?tab=hastalar` : "/Hastalar")}
        onDelete={() => setConfirmTerminate(true)}
        canDelete={canTerminate && Boolean(linkId)}
        tab={activeTab}
        onTabChange={(newTab) => {
          setActiveTab(newTab);
        }}
        tabs={visibleTabs}
      />

      <ConfirmDialog
        open={confirmTerminate}
        onClose={() => setConfirmTerminate(false)}
        onConfirm={handleTerminate}
        title={`${terms.patientAcc} Klinikten Çıkar`}
        message={`${terms.patientAcc} klinik kayıtlarından çıkarmak istediğinize emin misiniz?`}
        confirmText="Çıkar"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={terminating}
      />

      {isLockedByExamination && (
        <LockedByExaminationBanner exam={activeExamination} />
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          overflowX: "hidden",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          // Kartlı sekmede başlık şeridiyle aynı yatay hiza (design-principles §2)
          // Cihaz Ölçümleri kartlı içerik ama zemini beyaz (2026-10-07)
          ...(FLAT_TABS.has(activeTab)
            ? { p: 0, bgcolor: COLORS.white }
            : activeTab === "cihazlar"
              ? { ...CARD_PAGE_PAD, bgcolor: COLORS.white }
              : CARD_PAGE_PAD),
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Kişisel Bilgiler kendi kart iskeletini çizer (çerçeve + başlık hemen,
            iskelet yalnız alanlarda); diğer sekmeler için genel iskelet. */}
        {!initialLoadDone && FLAT_TABS.has(activeTab) && (
          // Düz sekme iskeleti: araç satırı + çizgiyle ayrılmış satırlar (kutusuz)
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 56,
                px: { xs: 2, md: 4 },
                borderBottom: `1px solid ${COLORS.slate200}`,
              }}
            >
              <Skeleton variant="text" width={140} height={20} />
              <Skeleton variant="rounded" width={150} height={30} sx={{ borderRadius: 2 }} />
            </Box>
            {[0, 1, 2, 3, 4].map((k) => (
              <Box
                key={k}
                sx={{
                  px: { xs: 2, md: 4 },
                  py: 1.5,
                  borderBottom: `1px solid ${COLORS.slate100}`,
                }}
              >
                <Skeleton variant="text" width={`${30 + (k % 3) * 12}%`} height={20} />
                <Skeleton variant="text" width={`${18 + (k % 2) * 10}%`} height={16} />
              </Box>
            ))}
          </Box>
        )}
        {!initialLoadDone && activeTab !== "kisisel-bilgiler" && !FLAT_TABS.has(activeTab) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Section card skeleton — başlık + form alanları */}
            {[0, 1].map((i) => (
              <Box
                key={i}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  bgcolor: COLORS.white,
                  border: `1px solid ${COLORS.slate200}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Skeleton variant="text" width={180} height={24} />
                  <Skeleton variant="rounded" width={90} height={28} sx={{ borderRadius: 2 }} />
                </Box>
                <Stack spacing={1.25}>
                  {[...Array(5)].map((_, k) => (
                    <Box key={k} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Skeleton variant="text" width={130} height={18} />
                      <Skeleton variant="text" width={`${40 + (k % 3) * 15}%`} height={20} sx={{ flex: 1 }} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Box>
        )}
        {/* Lazy mount: only the active tab is rendered (and thus fetches data).
            Switching tabs unmounts the previous one. */}
        {activeTab === "kisisel-bilgiler" && (
          <PersonalInfoTab
            mode="standalone"
            loading={!initialLoadDone}
            patientId={patientId}
            token={token}
            patientData={patient}
            onPatientSaved={(next) => setPatient(next)}
            canEdit={effectiveCanEdit}
          />
        )}

        {canSeeTreatments && initialLoadDone && activeTab === "tedaviler" && (
          <TreatmentsTab
            mode="standalone"
            patient={patient}
            patientId={patientId}
            token={token}
            canEdit={effectiveCanEdit}
            initialSelectedId={initialRecordId}
          />
        )}

        {canSeePrescriptions && initialLoadDone && activeTab === "receteler" && (
          <PrescriptionsTab
            mode="standalone"
            onGoToTreatments={() => setActiveTab("tedaviler")}
            patient={patient}
            patientId={patientId}
            token={token}
            canEdit={effectiveCanEdit}
          />
        )}

        {(canSeeDiet || showHydration) && initialLoadDone && activeTab === "beslenme" && (
          <NutritionTab
            patientId={patientId}
            patient={patient}
            token={token}
            canEdit={effectiveCanEdit}
            canSeeDiet={canSeeDiet}
            canSeeHydration={showHydration}
            usesApp={patientUsesApp}
            canManageHydration={canManageHydration && effectiveCanEdit}
            section={nutritionSection}
            onSectionChange={setNutritionSection}
          />
        )}

        {showMedPlans && initialLoadDone && activeTab === "ilac-alarmlari" && (
          <MedicationAlarmsTab patientId={patientId} token={token} canEdit={effectiveCanEdit} />
        )}

        {canSeeAccount && initialLoadDone && activeTab === "hesap" && (
          <AccountTab patientId={patientId} clinicId={clinicId} token={token} canEdit={canEditPatient} />
        )}

        {canSeeSessions && initialLoadDone && activeTab === "uygulamalar" && (
          <PracticeSessionsTab
            patient={patient}
            patientId={patientId}
            clinicId={clinicId}
            token={token}
            canEdit={effectiveCanEdit}
          />
        )}

        {canSeeAnamnez && initialLoadDone && activeTab === "anamnez" && (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <AnamnesisTab
              mode="standalone"
              patient={patient}
              patientId={patientId}
              token={token}
              canEdit={effectiveCanEdit}
            />
          </Box>
        )}

        {canSeeTetkik && initialLoadDone && activeTab === "tetkik" && (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <InvestigationTab
              mode="standalone"
              patientId={patientId}
              token={token}
              canEdit={effectiveCanEdit}
            />
          </Box>
        )}

        {hasCommunication && initialLoadDone && activeTab === "iletisim" && (
          <CommunicationTab
            patientId={patientId}
            token={token}
            canEdit={effectiveCanEdit}
            can={{
              messages: canSeeMessages && patientUsesApp,
              phoneCalls: canSeePhoneCalls,
              notifications: canSeeNotifications && patientUsesApp,
            }}
            section={communicationSection}
            onSectionChange={setCommunicationSection}
          />
        )}

        {canSeeAppointments && initialLoadDone && activeTab === "randevular" && (
          <AppointmentsTab
            mode="standalone"
            patientId={patientId}
            clinicId={clinicId}
            token={token}
            canEdit={effectiveCanEdit}
          />
        )}

        {canSeeDevices && initialLoadDone && activeTab === "cihazlar" && (
          <DeviceMeasurementsTab
            mode="standalone"
            patientId={patientId}
            clinicId={clinicId}
            token={token}
            patient={patient}
          />
        )}
      </Box>
    </Box>
  );
}
